// src/pages/battle/components/PixelSpriteIntegration.jsx
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Users, Skull, Shield, Target, Heart } from 'lucide-react';
import { UnitType } from '../../../utils/combatSystem';

// ============================================================================
// SPRITE MAPPING CONSTANTS
// ============================================================================

/**
 * Maps soldier class IDs to their sprite filenames
 */
const ALLY_SPRITE_MAP = {
  ranger: 'ranger_ally',
  sniper: 'sniper_ally',
  heavy: 'heavy_ally',
  support: 'support_ally'
};

/**
 * Maps enemy names/types to their sprite filenames
 * Updated to match actual file naming: capital E in _Ene
 */
const ENEMY_SPRITE_MAP = {
  devastator: 'devastator_Ene',
  harpy: 'harpy_Ene',
  juggernaut: 'juggernaut_ene',
  phantom: 'phantom_Ene',
  scavenger: 'scavercluser_ene',
  welder: 'welder_ene',
  acidtank: 'acidtank_boss_ene',
  // Add fallback for generic enemies
  enemy: 'devastator_Ene'
};

/**
 * Maps tile types to terrain sprite filenames
 */
const TILE_SPRITE_MAP = {
  0: null, // EMPTY
  1: 'cover_half', // HALF_COVER
  2: 'cover_full'  // FULL_COVER
};

/**
 * Fallback icons for each unit type
 */
const FALLBACK_ICONS = {
  soldier: Users,
  enemy: Skull,
  ranger: Target,
  sniper: Target,
  heavy: Shield,
  support: Heart,
  default: Users
};

// ============================================================================
// IMAGE CACHE SINGLETON
// ============================================================================

/**
 * Global image cache to prevent duplicate loads and improve performance
 */
class SpriteCache {
  constructor() {
    this.cache = new Map();
    this.loadingPromises = new Map();
  }

  /**
   * Get cached image or initiate load
   * @param {string} path - Image path
   * @returns {Promise<HTMLImageElement>}
   */
  load(path) {
    // Return cached image if available
    if (this.cache.has(path)) {
      return Promise.resolve(this.cache.get(path));
    }

    // Return existing loading promise if in progress
    if (this.loadingPromises.has(path)) {
      return this.loadingPromises.get(path);
    }

    // Create new loading promise
    const promise = new Promise((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => {
        this.cache.set(path, img);
        this.loadingPromises.delete(path);
        resolve(img);
      };

      img.onerror = () => {
        this.loadingPromises.delete(path);
        reject(new Error(`Failed to load sprite: ${path}`));
      };

      img.src = path;
    });

    this.loadingPromises.set(path, promise);
    return promise;
  }

  /**
   * Preload multiple sprites
   * @param {string[]} paths - Array of image paths
   */
  preload(paths) {
    paths.forEach(path => this.load(path).catch(() => {}));
  }

  /**
   * Clear cache (useful for testing or theme changes)
   */
  clear() {
    this.cache.clear();
    this.loadingPromises.clear();
  }
}

// Global cache instance
const spriteCache = new SpriteCache();

// ============================================================================
// CUSTOM HOOK: useImageCache
// ============================================================================

/**
 * Hook to load and cache sprite images
 * @param {string} spritePath - Path to sprite image
 * @param {boolean} preload - Whether to preload immediately
 * @returns {Object} { image, loading, error }
 */
const useImageCache = (spritePath, preload = false) => {
  const [state, setState] = useState({
    image: null,
    loading: true,
    error: false
  });

  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  useEffect(() => {
    if (!spritePath) {
      setState({ image: null, loading: false, error: true });
      return;
    }

    setState(prev => ({ ...prev, loading: true, error: false }));

    spriteCache.load(spritePath)
      .then(img => {
        if (isMounted.current) {
          setState({ image: img, loading: false, error: false });
        }
      })
      .catch(() => {
        if (isMounted.current) {
          setState({ image: null, loading: false, error: true });
        }
      });
  }, [spritePath]);

  // Preload on mount if requested
  useEffect(() => {
    if (preload && spritePath) {
      spriteCache.load(spritePath).catch(() => {});
    }
  }, [preload, spritePath]);

  return state;
};

// ============================================================================
// BASE COMPONENT: PixelSprite
// ============================================================================

/**
 * Base component for rendering pixel art sprites with automatic fallback
 * 
 * IMPORTANT: Sprites must be in public/themes/ folder for CRA to serve them
 * 
 * @param {string} type - Sprite type identifier (ranger_ally, devastator_Ene, etc.)
 * @param {string} theme - Theme name for constructing asset path
 * @param {number} size - Dimensions in pixels (e.g., 32, 48, 64)
 * @param {string} className - Additional CSS classes
 * @param {React.Component} fallbackIcon - Lucide icon component to use on error
 * @param {string} fallbackColor - Color class for fallback icon
 * @param {boolean} preload - Whether to preload sprite
 * @returns {JSX.Element}
 */
export const PixelSprite = ({ 
  type, 
  theme = 'vanilla',
  size = 32, 
  className = '', 
  fallbackIcon: FallbackIcon = Users,
  fallbackColor = 'text-blue-400',
  preload = false 
}) => {
  // Construct sprite path for public folder
  // CRA serves public/ files from root, so path is /themes/...
  const spritePath = useMemo(() => {
    if (!type) return null;
    
    // Determine if this is an ally or enemy sprite
    const isAlly = type.endsWith('_ally');
    const category = isAlly ? 'allies' : 'enemies';
    
    // Path relative to public/ folder
    return `/themes/${theme}/assets/mission/${category}/${type}.png`;
  }, [type, theme]);

  const { image, loading, error } = useImageCache(spritePath, preload);

  // Render fallback icon if loading or error
  if (loading || error || !image) {
    return (
      <FallbackIcon 
        size={size} 
        className={`${fallbackColor} ${className}`}
      />
    );
  }

  // Render pixel-perfect sprite
  return (
    <img
      src={image.src}
      alt={type}
      className={`pixel-sprite ${className}`}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        imageRendering: 'pixelated'
      }}
    />
  );
};

// ============================================================================
// SPECIALIZED COMPONENT: UnitSprite
// ============================================================================

/**
 * Intelligent unit sprite renderer that automatically selects correct sprite
 * based on unit type and class
 * 
 * @param {Object} unit - Battle unit object
 * @param {string} unit.type - UnitType.SOLDIER or UnitType.ENEMY
 * @param {string} unit.classId - ranger|sniper|support|heavy (for soldiers)
 * @param {string} unit.name - Enemy name or unit designation
 * @param {string} theme - Current theme name
 * @param {number} size - Display size in pixels
 * @param {string} className - Additional CSS classes
 * @param {string} primaryColor - Primary theme color class
 * @param {string} alertColor - Alert theme color class
 * @returns {JSX.Element}
 */
export const UnitSprite = ({ 
  unit, 
  theme = 'vanilla',
  size = 32, 
  className = '',
  primaryColor = 'text-blue-400',
  alertColor = 'text-red-400'
}) => {
  const spriteType = useMemo(() => {
    if (!unit) return null;

    // Handle soldier units
    if (unit.type === UnitType.SOLDIER) {
      const classId = unit.classId || 'ranger';
      return ALLY_SPRITE_MAP[classId] || ALLY_SPRITE_MAP.ranger;
    }

    // Handle enemy units
    if (unit.type === UnitType.ENEMY) {
      // Normalize enemy name: remove spaces, lowercase, extract key word
      const enemyName = (unit.name || unit.enemyType || 'enemy').toLowerCase();
      
      // Try to match enemy name to sprite map
      for (const [key, spriteFile] of Object.entries(ENEMY_SPRITE_MAP)) {
        if (enemyName.includes(key)) {
          return spriteFile;
        }
      }
      
      // Default fallback
      return ENEMY_SPRITE_MAP.enemy;
    }

    return null;
  }, [unit]);

  // Determine fallback icon
  const fallbackIcon = useMemo(() => {
    if (!unit) return FALLBACK_ICONS.default;
    
    if (unit.type === UnitType.SOLDIER) {
      return FALLBACK_ICONS[unit.classId] || FALLBACK_ICONS.soldier;
    }
    
    return FALLBACK_ICONS.enemy;
  }, [unit]);

  // Determine fallback color
  const fallbackColor = unit?.type === UnitType.SOLDIER ? primaryColor : alertColor;

  if (!spriteType) {
    const Icon = fallbackIcon;
    return <Icon size={size} className={`${fallbackColor} ${className}`} />;
  }

  return (
    <PixelSprite
      type={spriteType}
      theme={theme}
      size={size}
      className={className}
      fallbackIcon={fallbackIcon}
      fallbackColor={fallbackColor}
      preload={true}
    />
  );
};

// ============================================================================
// SPECIALIZED COMPONENT: TileSprite
// ============================================================================

/**
 * Renders terrain/cover sprites on tiles
 * 
 * @param {number} tileType - TileType constant (0=EMPTY, 1=HALF_COVER, 2=FULL_COVER)
 * @param {string} theme - Current theme name
 * @param {number} size - Dimensions in pixels
 * @param {string} className - Additional CSS classes
 * @returns {JSX.Element|null}
 */
export const TileSprite = ({ 
  tileType, 
  theme = 'vanilla',
  size = 60, 
  className = '' 
}) => {
  const spriteFile = TILE_SPRITE_MAP[tileType];

  const spritePath = spriteFile 
    ? `/themes/${theme}/assets/mission/tiles/${spriteFile}.png`
    : null;

  const { image, loading, error } = useImageCache(spritePath, true);

  // Don't render anything for empty tiles
  if (!spriteFile) return null;

  // Render nothing if loading or error (tiles are optional visual enhancement)
  if (loading || error || !image) {
    return null;
  }

  return (
    <img
      src={image.src}
      alt={`${spriteFile} terrain`}
      className={`pixel-sprite absolute inset-0 opacity-50 ${className}`}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        imageRendering: 'pixelated'
      }}
    />
  );
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Preload common sprites for better performance
 * Call this when battle initializes
 * 
 * @param {string} theme - Theme name
 * @param {Array} squad - Squad array with classId
 */
export const preloadBattleSprites = (theme = 'vanilla', squad = []) => {
  const paths = [];

  // Preload all ally sprites
  Object.values(ALLY_SPRITE_MAP).forEach(sprite => {
    paths.push(`/themes/${theme}/assets/mission/allies/${sprite}.png`);
  });

  // Preload common enemy sprites (with correct casing)
  ['devastator_Ene', 'harpy_Ene', 'phantom_Ene'].forEach(sprite => {
    paths.push(`/themes/${theme}/assets/mission/enemies/${sprite}.png`);
  });

  // Preload tile sprites
  ['cover_half', 'cover_full'].forEach(sprite => {
    paths.push(`/themes/${theme}/assets/mission/tiles/${sprite}.png`);
  });

  spriteCache.preload(paths);
};

/**
 * Clear sprite cache (useful for theme changes)
 */
export const clearSpriteCache = () => {
  spriteCache.clear();
};

// ============================================================================
// EXPORTS
// ============================================================================

const PixelSpriteIntegration = {
  PixelSprite,
  UnitSprite,
  TileSprite,
  preloadBattleSprites,
  clearSpriteCache
};

export default PixelSpriteIntegration;